import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  Chip,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  Menu,
} from '@mui/material';
import apiService from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

// Define components
function Models() {
  const [models, setModels] = useState([]); //state that stores model list, initially empty list []
  const [filteredModels, setFilteredModels] = useState([]);
  const [loading, setLoading] = useState(true); //boolean, data loading in progress, initially true
  const [error, setError] = useState(''); //string for storing errors for API calls, initially empty ""

  // filters & search
  const [activeFilter, setActiveFilter] = useState('all');
  const [deletedFilter, setDeletedFilter] = useState('all');
  const [search, setSearch] = useState('');

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // navigation for model details
  const navigate = useNavigate();

  // Fetch Data From API/Async Function Loading Models
  const loadModels = async () => {
    setLoading(true);
    setError('');

    const isActive = activeFilter === 'all' ? null : activeFilter === 'active';
    const isDeleted =
      deletedFilter === 'all' ? null : deletedFilter === 'deleted';

    const res = await apiService.fetchModels(isActive, isDeleted); // using apiService, returns object { success: true, data: [...]}

    if (res.success) {
      //write data in models state
      setModels(res.data);
      setFilteredModels(res.data);
    } else {
      //write in error state
      setError(res.error);
    }

    setLoading(false); //stop loading
  };

  useEffect(() => {
    loadModels(); //func executes once when the component loads first time
  }, [activeFilter, deletedFilter]);

  // search filter
  useEffect(() => {
    let filtered = models.filter(
      (m) =>
        m.model_name.toLowerCase().includes(search.toLowerCase()) ||
        m.organization_name.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredModels(filtered);
    setPage(0);
  }, [search, models]);

  // pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h5" mb={2}>
        Models
      </Typography>

      {/* Filters */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField
          label="Search by model/org"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <FormControl>
          <InputLabel>Active</InputLabel>
          <Select
            value={activeFilter}
            label="Active"
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Deleted</InputLabel>
          <Select
            value={deletedFilter}
            label="Deleted"
            onChange={(e) => setDeletedFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="deleted">Deleted</MenuItem>
            <MenuItem value="not_deleted">Not Deleted</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100vh"
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Conditional Rendering */}
      {!loading && !error && (
        <TableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* Stats column */}
                <TableCell>
                  <strong>Statistics</strong>
                </TableCell>

                <TableCell>
                  <strong>Model Id</strong>
                </TableCell>
                <TableCell>
                  <strong>Model Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Org Id</strong>
                </TableCell>
                <TableCell>
                  <strong>Org Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Active</strong>
                </TableCell>
                <TableCell>
                  <strong>Deleted</strong>
                </TableCell>
              </TableRow>
            </TableHead>

            {/* models.map(...) render row for every model */}
            <TableBody>
              {filteredModels
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((m) => (
                  <TableRow
                    key={m.model_id}
                    hover
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/models/${m.model_id}`)}
                  >
                    {/* Statistics */}
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          label={`Total: ${m.total_predictions}`}
                          color="primary"
                          size="small"
                        />
                        <Chip
                          label={`24h: ${m.prediction_last_24h}`}
                          color="secondary"
                          size="small"
                        />
                        <Chip
                          label={`Median: ${m.median_latency}ms`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`Avg: ${
                            m.avg_prediction_value
                              ? m.avg_prediction_value.toFixed(2)
                              : 0
                          }`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>

                    {/* Main Data */}
                    <TableCell>{m.model_id}</TableCell>
                    <TableCell>{m.model_name}</TableCell>
                    <TableCell>{m.organization_id}</TableCell>
                    <TableCell>{m.organization_name}</TableCell>

                    <TableCell>
                      <Chip
                        label={m.is_active ? 'Yes' : 'No'}
                        color={m.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={m.is_deleted ? 'Yes' : 'No'}
                        color={m.is_deleted ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredModels.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      )}
    </Box>
  );
}

export default Models;
